import * as React from "react";
import { useHistory } from "react-router-dom";

import { ITerminology } from "src/library/utils";
import {
  Modal,
  IModalProps
} from "src/library/components/Modal";
import { IOrder } from "src/core/orders/api/orders.api";

import { TransactionStatus } from "../../api/transactions.api";

interface IOrderPaymentErrorNoticeModalProps {
  terminology: ITerminology;
  order: IOrder;
}

interface IModalDataAttributes {
  title: NonNullable<IModalProps["title"]>;
  body: NonNullable<IModalProps["body"]>;
  actions: NonNullable<IModalProps["actions"]>;
}

/**
 * Order payment error notice modal
 */
const OrderPaymentErrorNoticeModal: React.ReactFCWithChildren<IOrderPaymentErrorNoticeModalProps> = ({
  terminology,
  order
}) => {
  const history = useHistory();
  const [ showModal, setShowModal ] = React.useState(false);

  // Set the modalData according to the transaction error (if any) and status
  const modalData: IModalDataAttributes | null = React.useMemo(() => {
    const lastTransaction = order?.transactions ? order.transactions[ 0 ] : null;
    let title: IModalDataAttributes["title"] = "Payment required";
    let body: IModalDataAttributes["body"] = "";

    let actions: IModalDataAttributes["actions"] = [
      {
        $buttonType: "primary",
        name: "Pay now",
        action: () => history.push(`/orders/${order.id}/checkout`)
      },
      {
        $buttonType: "secondary",
        name: "Pay later",
        action: () => setShowModal(false)
      }

    ];

    // If there is no lastTransaction data, or the last transaction wasnt a failure status return null
    if (!lastTransaction || [
      TransactionStatus.CANCELED,
      TransactionStatus.PROCESSING,
      TransactionStatus.SUCCEEDED
    ].includes(lastTransaction.status)) {
      return null;
    }

    // Output an error for the following logic
    if (lastTransaction.errorReason) {
      // Logic according the the error reason type
      switch (lastTransaction.errorReason.type) {
        case "api_error":
        case "card_error":
          switch (lastTransaction.errorReason.code) {
            case "insufficient_funds":
              {
                title = "Payment failure";
                body = `${lastTransaction.errorReason.message.replace(/\.$/, "")} (${lastTransaction.errorReason.code}). Please add funds to your account and then manually try again to authorize the payment.<br />`;
              }
              break;

            // The customer needs to contact their card issuer for more
            // information and try again.
            case "approve_with_id":
            case "call_issuer":
            case "card_not_supported":
            case "card_velocity_exceeded":
            case "currency_not_supported":
            case "do_not_honor":
            case "do_not_try_again":
            case "fraudulent":
            case "generic_decline":
            case "invalid_account":
            case "invalid_amount":
            case "lost_card":
            case "merchant_blacklist":
            case "new_account_information_available":
            case "no_action_taken":
            case "not_permitted":
            case "pickup_card":
            case "restricted_card":
            case "revocation_of_all_authorizations":
            case "revocation_of_authorization":
            case "security_violation":
            case "service_not_allowed":
            case "stolen_card":
            case "stop_payment_order":
            case "transaction_not_allowed":
              title = "Payment resolution required";
              body = `${lastTransaction.errorReason.message.replace(/\.$/, "")} (${lastTransaction.errorReason.code}). Please contact your card issuer to resolve this matter. Once resolved please try again.<br />`;
              body += "<br />";
              body += `If this issue cannot be resolved, please select a new/existing payment method for all the associated ${terminology.drop.plural.text} via the Payment settings screen. Once done you will need to manually complete payment for this order.`;

              break;

            // The customer should use an alternative payment method.
            case "expired_card":
            case "pin_try_exceeded":
            case "testmode_decline":
            case "withdrawal_count_limit_exceeded":
              title = "Alternative payment method required";
              body = `${lastTransaction.errorReason.message.replace(/\.$/, "")} (${lastTransaction.errorReason.code}).<br />`;
              body += "<br />";
              body += `To resolve this issue, you will be required to select a new/existing payment method for all the associated ${terminology.drop.plural.text} via the Payment settings screen. Once done you will need to manually complete payment for this order.`;

              actions = [
                {
                  $buttonType: "primary",
                  name: "Payment methods",
                  action: () => history.push("/settings/user#payment_methods")
                },
                {
                  $buttonType: "secondary",
                  name: "OK",
                  action: () => setShowModal(false)
                }
              ];

              break;

            // The payment should be attempted again. If it still cannot be
            // processed, the customer needs to contact their card issuer.
            default:
            case "authentication_required":
            case "incorrect_number":
            case "incorrect_cvc":
            case "incorrect_pin":
            case "incorrect_zip":
            case "invalid_cvc":
            case "invalid_expiry_month":
            case "invalid_expiry_year":
            case "invalid_number":
            case "invalid_pin":
            case "issuer_not_available":
            case "offline_pin_required":
            case "online_or_offline_pin_required":
            case "processing_error":
            case "reenter_transaction":
            case "try_again_later":
              title = "Payment failure";
              body = `${lastTransaction.errorReason.message.replace(/\.$/, "")} (${lastTransaction.errorReason.code}). Please manually try again to authorize the payment.<br />`;
              body += "<br />";
              body += "If you continue to have the same issue, please contact your card issuer to resolve this matter. Once resolved please try again.";
          }
          break;

        case "authentication_error":

          // Logic according the the error reason code
          switch (lastTransaction.errorReason.code) {
            case "authentication_required":
              title = "Authorization required";
              body = "Payment could not be taken for this order as it requires authorization from the card holder. Please manually complete payment for this order (payment authorization will be required).";
              break;

            default:
              body = "An unknown error has occurred while attempting to take payment for this order. Please contact one of the administrators.";
          }
      }

      setShowModal(true);
    } else {
      switch (lastTransaction.status) {
        case TransactionStatus.REQUIRES_ACTION:
          title = "Authorization required";
          body = "Payment could not be taken for this order as it requires authorization from the card holder. Please manually complete payment for this order (payment authorization will be required).";
          setShowModal(true);
          break;

        default:
          return null;
      }
    }

    return {
      title,
      body,
      actions
    };
  }, [
    history,
    order,
    terminology.drop.plural.text
  ]);

  // Return an empty component if copy is null
  if (!modalData) {
    return null;
  }

  return (
    <Modal
      type="notice"
      show={showModal}
      backgroundAction={() => setShowModal(false)}
      title={modalData.title}
      body={modalData.body}
      actions={modalData.actions}
    />
  );
};

export default OrderPaymentErrorNoticeModal;
